import React from 'react';
import { Badge, Button, Modal } from 'react-bootstrap';
import keywordBadgeStyle from '../../../../helpers/keywordBadgeStyle';
import { formatDateTime } from '../../../../helpers/formatDate';

// Plain-language meaning of each outcome, so the log explains itself instead of
// needing the reader to know the pipeline.
const OUTCOMES = {
    transferred: {
        label: 'Transferred',
        variant: 'success',
        summary: 'AI confirmed exactly one rule, and the ticket was moved.',
    },
    skipped_no_match: {
        label: 'No match',
        variant: 'secondary',
        summary:
            'No active rule applied to this ticket, so AI was never asked. Usually the rule is ' +
            'limited to specific incoming emails and this ticket arrived somewhere else.',
    },
    skipped_ai_rejected: {
        label: 'AI rejected',
        variant: 'info',
        summary: 'AI read the ticket against the rule description and decided it does not belong there.',
    },
    skipped_ambiguous_match: {
        label: 'Ambiguous',
        variant: 'warning',
        summary:
            'More than one rule was confirmed for this ticket. Rather than guess a destination, ' +
            'the ticket was left where it is.',
    },
    skipped_error: {
        label: 'Error',
        variant: 'danger',
        summary:
            'The routing decision could not be completed, or the transfer itself was refused. ' +
            'The ticket was left where it is.',
    },
};

const Row = ({ label, children }) => (
    <div className="d-flex flex-wrap py-2 border-bottom">
        <div className="text-muted small" style={{ minWidth: 170 }}>
            {label}
        </div>
        <div className="flex-grow-1" style={{ minWidth: 200 }}>
            {children}
        </div>
    </div>
);

const AutoTransferLogModal = ({ show, onHide, log }) => {
    if (!log) return null;

    const outcome = OUTCOMES[log.outcome] || {
        label: log.outcome,
        variant: 'secondary',
        summary: '',
    };
    const transferred = log.outcome === 'transferred';
    const destination = log.to_workspace || log.intended_destination;

    return (
        <Modal show={show} onHide={onHide} centered size="lg">
            <Modal.Header closeButton>
                <Modal.Title style={{ fontSize: 18 }}>Auto-transfer decision</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="d-flex align-items-center gap-2 mb-2">
                    <Badge bg={outcome.variant} text={outcome.variant === 'warning' ? 'dark' : undefined}>
                        {outcome.label}
                    </Badge>
                    {log.ai_invoked ? (
                        <span className="text-muted small">AI was asked</span>
                    ) : (
                        <span className="text-muted small">AI was not asked</span>
                    )}
                </div>
                {outcome.summary ? <p className="text-muted small">{outcome.summary}</p> : null}

                <Row label="Ticket">
                    {log.ticket ? (
                        <>
                            <span className="fw-semibold">#{log.ticket.ticket_id || log.ticket.id}</span>
                            {log.ticket.title ? <span> — {log.ticket.title}</span> : null}
                        </>
                    ) : (
                        <span className="text-muted">Ticket no longer exists</span>
                    )}
                </Row>

                <Row label="Rule">
                    {log.rule?.name || <span className="text-muted">No single rule (see reason below)</span>}
                </Row>

                <Row label={transferred ? 'Moved to' : 'Intended destination'}>
                    {destination?.name || <span className="text-muted">—</span>}
                    {!transferred && destination ? (
                        <span className="text-muted small"> (not moved)</span>
                    ) : null}
                </Row>

                <Row label="Keywords found">
                    {log.matched_keywords?.length > 0 ? (
                        <>
                            <div className="d-flex flex-wrap gap-1 mb-1">
                                {log.matched_keywords.map((k) => (
                                    <Badge key={k} bg="secondary" style={keywordBadgeStyle}>
                                        {k}
                                    </Badge>
                                ))}
                            </div>
                            <div className="text-muted small">
                                Context only — keywords never decide the outcome on their own.
                            </div>
                        </>
                    ) : (
                        <span className="text-muted">
                            None — a ticket can still match on meaning alone
                        </span>
                    )}
                </Row>

                {log.ai_reason ? (
                    <Row label="AI reasoning">
                        <span className="fst-italic">&ldquo;{log.ai_reason}&rdquo;</span>
                    </Row>
                ) : null}

                {log.error_message ? (
                    <Row label="What blocked it">
                        <span className="text-danger">{log.error_message}</span>
                    </Row>
                ) : null}

                <Row label="When">
                    {log.matched_at ? formatDateTime(log.matched_at) : '—'}
                </Row>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onHide}>
                    Close
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default AutoTransferLogModal;
